import * as React from 'react';
import {Slot, Slottable} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../lib/utils';
import {IconComponent, Icons} from '../icons';
import {Tooltip, TooltipContent, TooltipTrigger} from './tooltip';
import {TooltipContentProps} from '@radix-ui/react-tooltip';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-normal shadow-xs transition-colors focus-visible:outline-hidden focus-visible:border-ring-muted focus-visible:ring-2 focus-visible:ring-border data-[state=open]:border-ring-muted data-[state=open]:ring-2 data-[state=open]:ring-border disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border border-white/30 bg-primary text-primary-foreground dark:font-medium hover:bg-primary/80 dark:hover:bg-[hsl(24_5.7%_82.7%)]',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background font-medium dark:font-normal hover:bg-border-subtle',
        secondary:
          'border border-input bg-card text-card-foreground font-medium dark:font-normal dark:bg-[hsl(33_5.8%_25%)] hover:bg-border-subtle dark:hover:bg-border-subtle',
        ghost:
          'shadow-none font-medium dark:font-normal hover:bg-border-subtle',
        link: 'text-primary font-medium shadow-none underline-offset-4 hover:underline',
        background: cn(
          'text-muted-foreground shadow-none hover:bg-primary/70 hover:text-primary-foreground border border-transparent',
          'data-[active=true]:bg-primary/90 data-[active=true]:text-primary-foreground data-[active=true]:border-sidebar-border',
        ),
        darkOutline:
          'gap-2 border border-border bg-secondary text-foreground hover:bg-tertiary/50',
        darkMuted:
          'gap-2 bg-muted-foreground/30 text-secondary-foreground hover:bg-muted-foreground/50',
      },
      size: {
        default: 'h-8 px-3 py-2',
        sm: 'h-7 px-2.5 text-xs',
        lg: 'h-9 px-4 py-2',
        icon: 'h-8 w-8 min-w-8 min-h-8',
        'icon-sm': 'h-7 w-7 min-w-7 min-h-7',
        fill: 'w-full h-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  StartIcon?: IconComponent;
  EndIcon?: IconComponent;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  tooltip?: string;
  tooltipOptions?: TooltipContentProps;
  tooltipDelayDuration?: number;
  onTooltipOpenChange?: (open: boolean) => void;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      loading,
      variant,
      size,
      asChild = false,
      children,
      disabled,
      startIcon,
      endIcon,
      StartIcon,
      EndIcon,
      tooltip,
      tooltipOptions,
      tooltipDelayDuration,
      onTooltipOpenChange,
      ...props
    },
    ref,
  ) => {
    let resolvedStartIcon = startIcon;
    let resolvedEndIcon = endIcon;

    if (StartIcon) {
      resolvedStartIcon = <StartIcon className="w-4 h-4 shrink-0" />;
    }
    if (EndIcon) {
      resolvedEndIcon = <EndIcon className="w-4 h-4" />;
    }

    resolvedStartIcon = resolvedStartIcon ? (
      <Slot className="mr-2">{resolvedStartIcon}</Slot>
    ) : undefined;
    resolvedEndIcon = resolvedEndIcon ? (
      <Slot className="ml-2">{resolvedEndIcon}</Slot>
    ) : undefined;

    let resolvedChildren: React.ReactNode;
    if (!asChild && loading) {
      if (size === 'icon' || size === 'icon-sm') {
        resolvedChildren = <Icons.spinner className="w-4 h-4 animate-spin" />;
        resolvedStartIcon = undefined;
        resolvedEndIcon = undefined;
      } else {
        resolvedStartIcon = undefined;
        resolvedEndIcon = undefined;
        resolvedChildren = (
          <>
            <Icons.spinner className="mr-2 w-4 h-4 animate-spin" />
            {children}
          </>
        );
      }
    } else {
      resolvedChildren = children;
    }

    const Comp = asChild ? Slot : 'button';

    let buttonMarkup = (
      <Comp
        className={cn(
          resolvedStartIcon && 'pl-2',
          buttonVariants({variant, size, className}),
        )}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {resolvedStartIcon}
        <Slottable>{resolvedChildren}</Slottable>
        {resolvedEndIcon}
      </Comp>
    );

    if (tooltip) {
      if (disabled) {
        // A disabled button has `pointer-events: none`, so it can't fire the
        // hover events the tooltip needs. We wrap it in a span that acts as the
        // tooltip trigger's hover surface. That span must stretch to full width
        // when the button is full-width, otherwise it collapses to content
        // width and the button's `w-full` only fills the shrunken span.
        const isFullWidth =
          size === 'fill' || (className ?? '').split(/\s+/).includes('w-full');
        buttonMarkup = (
          <span className={cn('cursor-auto', isFullWidth && 'flex w-full')}>
            {buttonMarkup}
          </span>
        );
      }
      buttonMarkup = (
        <Tooltip
          onOpenChange={onTooltipOpenChange}
          delayDuration={tooltipDelayDuration}
        >
          <TooltipTrigger asChild>{buttonMarkup}</TooltipTrigger>
          <TooltipContent {...tooltipOptions}>{tooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return buttonMarkup;
  },
);
Button.displayName = 'Button';

export {Button, buttonVariants};
